import type { AuthedRequest } from '../auth/AuthProvider';

export type Severity = 'info' | 'warning' | 'critical';

/** Mirrors the event DTO of /api/v1/events (ADR-016). */
export interface VmsEvent {
  id: string;
  kind: string;
  severity: Severity;
  /** Null for events not tied to a device; name is empty if it was deleted. */
  device: { id: string; name: string } | null;
  occurred_at: string;
  data: Record<string, unknown>;
  /** Warning or critical, not acknowledged yet. */
  pending: boolean;
  acknowledged_at: string | null;
  acknowledged_by: { id: string; name: string } | null;
  ack_note: string | null;
}

export interface EventPage {
  items: VmsEvent[];
  next_before: string | null;
  /** The organisation's pending events (capped at 1000). */
  pending: number;
}

export interface EventFilter {
  severity: '' | Severity;
  kind: string;
  device: string;
}

export const eventsKey = ['events'] as const;

export function listEvents(req: AuthedRequest, f: EventFilter, before: string): Promise<EventPage> {
  const p = new URLSearchParams({ limit: '50' });
  if (f.severity) p.set('severity', f.severity);
  if (f.kind) p.set('kind', f.kind);
  if (f.device) p.set('device_id', f.device);
  if (before) p.set('before', before);
  return req<EventPage>('GET', `/api/v1/events?${p.toString()}`);
}

/** A camera's events of one kind in one time bucket (GET /devices/{id}/event-marks). */
export interface EventMark {
  kind: string;
  severity: Severity;
  count: number;
  first_at: string;
  last_at: string;
}

/** The marks of [from, to) grouped per bucket (seconds) by the server. */
export async function listEventMarks(
  req: AuthedRequest,
  deviceId: string,
  from: number,
  to: number,
  bucketSeconds: number,
): Promise<EventMark[]> {
  const p = new URLSearchParams({
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
    bucket: String(bucketSeconds),
  });
  const body = await req<{ items: EventMark[] }>(
    'GET',
    `/api/v1/devices/${encodeURIComponent(deviceId)}/event-marks?${p.toString()}`,
  );
  return body.items;
}

export const kindLabels: Record<string, string> = {
  'device.offline': 'Dispositivo offline',
  'device.error': 'Falha no dispositivo',
  'device.online': 'Dispositivo online',
  'video.signal_loss': 'Perda de vídeo',
  'video.signal_restored': 'Vídeo restabelecido',
  'video.tamper': 'Câmera obstruída',
  'video.tamper_cleared': 'Obstrução encerrada',
  'video.motion': 'Movimento detectado',
  'audio.sound': 'Som detectado',
  'alarm.input': 'Entrada de alarme acionada',
  'alarm.input_cleared': 'Entrada de alarme normalizada',
  'video.line_crossing': 'Cruzamento de linha',
  'video.intrusion': 'Invasão de área',
  'video.person': 'Pessoa detectada',
  'video.vehicle': 'Veículo detectado',
  'device.clock_invalid': 'Relógio do aparelho zerado',
  'device.clock_skew': 'Relógio do aparelho fora do horário',
  'device.clock_ok': 'Relógio do aparelho corrigido',
};

/** What a smart detection saw. */
const objectLabels: Record<string, string> = {
  human: 'pessoa',
  vehicle: 'veículo',
  motorvehicle: 'veículo',
  nonmotor: 'bicicleta ou moto',
};

export const severityLabels: Record<Severity, string> = {
  critical: 'Crítico',
  warning: 'Alerta',
  info: 'Informativo',
};

/** Short labels for the failure codes of device checks. */
export const reasonLabels: Record<string, string> = {
  unreachable: 'Inacessível pela rede',
  timeout: 'Sem resposta',
  unauthorized: 'Credenciais recusadas',
  blocked: 'Bloqueado pela política de rede',
  not_onvif: 'Serviço ONVIF não encontrado',
  not_supported: 'Recurso não suportado',
  protocol_error: 'Resposta inválida',
  not_rtsp: 'Porta não é RTSP',
  stream_not_found: 'Stream não encontrado',
  not_publishing: 'Não está enviando vídeo (RTMP)',
};

export const statusLabels: Record<string, string> = {
  online: 'online',
  offline: 'offline',
  error: 'com falha',
  unknown: 'desconhecido',
};

/** "8 min adiantado", "3 h atrasado", "2 dias atrasado". */
export function skewText(seconds: number): string {
  const abs = Math.abs(seconds);
  const amount =
    abs < 3600
      ? `${String(Math.max(1, Math.round(abs / 60)))} min`
      : abs < 172_800
        ? `${String(Math.round(abs / 3600))} h`
        : `${String(Math.round(abs / 86_400))} dias`;
  return `${amount} ${seconds > 0 ? 'adiantado' : 'atrasado'}`;
}

/**
 * One line describing what happened, from the event's details. `absolute`
 * formats instants in the organisation's time zone.
 */
export function eventDetail(e: VmsEvent, absolute?: (iso: string) => string): string {
  if (e.kind.startsWith('device.clock_')) {
    const at = typeof e.data.device_time === 'string' ? e.data.device_time : '';
    const skew = typeof e.data.skew_seconds === 'number' ? e.data.skew_seconds : 0;
    const parts: string[] = [];
    if (at) parts.push(`relógio do aparelho: ${absolute ? absolute(at) : at}`);
    if (e.kind === 'device.clock_skew') parts.push(skewText(skew));
    if (e.kind !== 'device.clock_ok')
      parts.push(
        e.data.time_mode === 'manual'
          ? 'hora manual: ative o NTP no aparelho'
          : 'gravações com horário errado',
      );
    return parts.join(' · ');
  }
  const reason = typeof e.data.reason === 'string' ? e.data.reason : '';
  const from = typeof e.data.from === 'string' ? e.data.from : '';
  const parts: string[] = [];
  if (typeof e.data.source === 'string') parts.push(`canal ${e.data.source}`);
  // Smart detections of Intelbras/Dahua devices (ADR-031).
  if (typeof e.data.object === 'string') parts.push(objectLabels[e.data.object] ?? e.data.object);
  if (typeof e.data.rule === 'string') parts.push(`regra "${e.data.rule}"`);
  if (reason) parts.push(reasonLabels[reason] ?? reason);
  if (from) parts.push(`antes: ${statusLabels[from] ?? from}`);
  return parts.join(' · ');
}
